'use client';

import { createContext, useContext, useEffect, useMemo, useState, PropsWithChildren } from 'react';

import { useConfigValue } from '@/components/providers/config-provider';
import { checkStorageAccess } from '@/lib/assistant-cloud-local/stores/indexeddb/db';

interface StorageAccessContextValue {
  hasStorageAccess: boolean;
}

const StorageAccessContext = createContext<StorageAccessContextValue | null>(null);

export function useStorageAccess(): StorageAccessContextValue {
  const context = useContext(StorageAccessContext);
  if (!context) {
    throw new Error('useStorageAccess must be used within StorageAccessProvider');
  }
  return context;
}

export function StorageAccessProvider({ children }: PropsWithChildren) {
  // Server-persistence tenants never open IndexedDB — skipping the probe both avoids the
  // IDB touch and keeps thread management on for browsers that block storage.
  const isServerPersistence = useConfigValue<'local' | 'server'>('persistence') === 'server';
  const [hasStorageAccess, setHasStorageAccess] = useState<boolean>(true);

  useEffect(() => {
    if (isServerPersistence) return;
    checkStorageAccess()
      .then(setHasStorageAccess)
      .catch(() => setHasStorageAccess(false));
  }, [isServerPersistence]);

  const value = useMemo(() => ({ hasStorageAccess }), [hasStorageAccess]);

  return <StorageAccessContext.Provider value={value}>{children}</StorageAccessContext.Provider>;
}
